import { resetPasswordVisibility } from "./password_visibility.js";
import { startConfirmationCooldown } from "./confirmation_cooldown.js";

import { openDrawer } from "./drawer.js";

const rootSelector = "[data-membership-root]";
const introFormSelector = "[data-membership-intro-form]";
const introEmailSelector = "[data-membership-email]";
const registrationFormSelector = "[data-membership-registration-form]";
const registrationEmailSelector = "[data-membership-registration-email]";
const registrationEmailHiddenSelector = "[data-membership-registration-email-hidden]";
const registrationEmailLabelSelector = "[data-membership-registration-email-label]";
const loginFormSelector = "[data-membership-login-form]";
const loginEmailSelector = "[data-membership-login-email]";
const loginEmailHiddenSelector = "[data-membership-login-email-hidden]";
const loginEmailLabelSelector = "[data-membership-login-email-label]";
const resetInstructionMinimumBusyTime = 500;

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content || "";
}

async function jsonRequest(url, { method = "POST", body } = {}) {
  const response = await fetch(url, {
    method,
    body,
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "X-CSRF-Token": csrfToken(),
    },
  });

  let data = {};
  try {
    data = await response.json();
  } catch (_error) {
    data = {};
  }

  return { response, data };
}

function focusAfterScreenChange(element) {
  if (!element) return;
  requestAnimationFrame(() => element.focus({ preventScroll: true }));
}

function showScreen(root, screenName, focusTarget) {
  if (!root) return;

  resetPasswordVisibility(root);

  root.querySelectorAll("[data-membership-screen]").forEach((screen) => {
    const active = screen.dataset.membershipScreen === screenName;
    screen.hidden = !active;
    screen.setAttribute("aria-hidden", (!active).toString());
  });

  root.dataset.membershipCurrentScreen = screenName;
  root.closest("[data-drawer]")?.scrollTo({ top: 0 });
  focusAfterScreenChange(focusTarget);
}

function feedbackElement(root, screen) {
  return root?.querySelector(`[data-membership-feedback="${screen}"]`);
}

function clearFeedback(root, screen) {
  const feedback = feedbackElement(root, screen);
  if (!feedback) return;
  feedback.replaceChildren();
  feedback.hidden = true;
}

function showFeedback(root, screen, messages) {
  const feedback = feedbackElement(root, screen);
  if (!feedback) return;

  const values = Array.isArray(messages) ? messages : [messages];
  feedback.replaceChildren();

  if (values.length === 1) {
    feedback.textContent = values[0];
  } else {
    const list = document.createElement("ul");
    list.className = "list-disc pl-5 text-left";
    values.forEach((message) => {
      const item = document.createElement("li");
      item.textContent = message;
      list.append(item);
    });
    feedback.append(list);
  }

  feedback.hidden = false;
}

function errorMessages(data, fallback) {
  const errors = Object.values(data?.errors || {}).flat().filter(Boolean);
  return errors.length ? errors : [fallback];
}

function setBusy(button, busy) {
  if (!button) return;
  button.dataset.busy = busy.toString();
  button.disabled = busy;
  button.setAttribute("aria-busy", busy.toString());
  button.classList.toggle("cursor-wait", busy);
  button.querySelectorAll("[data-membership-idle-content]").forEach((content) => {
    content.hidden = busy;
  });
  button.querySelectorAll("[data-membership-busy-content]").forEach((content) => {
    content.hidden = !busy;
  });
}

function wait(milliseconds) {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

function updateIntroForm(form) {
  if (!form) return;
  const email = form.querySelector(introEmailSelector);
  const valid = email?.value.trim().length >= (email.minLength || 5);
  form.querySelectorAll("[data-membership-action]").forEach((button) => {
    button.disabled = !valid;
  });
}

function syncCarriedEmail(root, type, value, lock) {
  const visibleSelector = type === "registration" ? registrationEmailSelector : loginEmailSelector;
  const hiddenSelector = type === "registration" ? registrationEmailHiddenSelector : loginEmailHiddenSelector;
  const labelSelector = type === "registration" ? registrationEmailLabelSelector : loginEmailLabelSelector;
  const visible = root.querySelector(visibleSelector);
  const hidden = root.querySelector(hiddenSelector);
  if (!visible || !hidden) return;

  visible.value = value;
  visible.disabled = lock;
  hidden.value = value;

  const label = root.querySelector(labelSelector);
  if (label) label.hidden = lock;
}

function syncEditableCarriedEmail(root, type) {
  const visibleSelector = type === "registration" ? registrationEmailSelector : loginEmailSelector;
  const hiddenSelector = type === "registration" ? registrationEmailHiddenSelector : loginEmailHiddenSelector;
  const visible = root.querySelector(visibleSelector);
  const hidden = root.querySelector(hiddenSelector);
  if (visible && hidden) hidden.value = visible.value.trim();
}

function updateRegistrationForm(form) {
  if (!form) return;
  const email = form.querySelector(registrationEmailHiddenSelector);
  const requiredFields = Array.from(form.querySelectorAll("[data-membership-required-field]"));
  const password = form.querySelector("[data-membership-password]");
  const terms = form.querySelector("[data-membership-privacy]");
  const submit = form.querySelector("[data-membership-submit]");
  if (!email || !password || !terms || !submit) return;

  const validEmail = email.value.trim().length >= 5;
  const requiredPresent = requiredFields.every((field) => field.value.trim().length > 0);
  const validPassword = password.value.length >= (password.minLength || 8);
  submit.disabled = submit.dataset.busy === "true" || !(validEmail && requiredPresent && validPassword && terms.checked);
}

function updateLoginForm(form) {
  if (!form) return;
  const email = form.querySelector(loginEmailHiddenSelector);
  const password = form.querySelector("[data-membership-login-password]");
  const submit = form.querySelector("[data-membership-login-submit]");
  if (!email || !password || !submit) return;

  submit.disabled = submit.dataset.busy === "true" || email.value.trim().length < 5 || password.value.trim().length === 0;
}

function showRegistration(root) {
  const value = root.querySelector(introEmailSelector)?.value.trim() || "";
  syncCarriedEmail(root, "registration", value, value.length > 0);
  updateRegistrationForm(root.querySelector(registrationFormSelector));
  showScreen(root, "registration", root.querySelector("[data-membership-registration-heading]"));
}

function showLogin(root) {
  const value = root.querySelector(introEmailSelector)?.value.trim() || "";
  syncCarriedEmail(root, "login", value, value.length > 0);
  updateLoginForm(root.querySelector(loginFormSelector));
  showScreen(root, "login", root.querySelector("[data-membership-login-heading]"));
}

function openMembershipDrawer(screenName, trigger) {
  const root = document.querySelector(rootSelector);
  if (!root) return;

  if (screenName === "registration") showRegistration(root);
  if (screenName === "login") showLogin(root);

  const drawer = root.closest("[data-drawer]");
  const fallbackTrigger = document.querySelector('[data-drawer-open="membership-panel"]');
  openDrawer(drawer, trigger || fallbackTrigger);
}

function showConfirmation(root) {
  showScreen(root, "confirmation", root.querySelector("[data-membership-confirmation-heading]"));
}

function clearLoginDraft(root) {
  const form = root.querySelector(loginFormSelector);
  const password = form?.querySelector("[data-membership-login-password]");
  if (password) password.value = "";
  updateLoginForm(form);
}

function showResetSent(root) {
  clearLoginDraft(root);
  showScreen(root, "reset-sent", root.querySelector("[data-membership-reset-sent-heading]"));
}

function currentEmail(root) {
  return root.querySelector(loginEmailHiddenSelector)?.value.trim() ||
    root.querySelector(registrationEmailHiddenSelector)?.value.trim() ||
    root.querySelector(introEmailSelector)?.value.trim() || "";
}

async function sendEmailInstructions(root, button, screen, successMessage, options = {}) {
  if (button.disabled) return;
  const { minimumBusyTime = 0, onSuccess } = options;
  let retryAfter;
  setBusy(button, true);
  clearFeedback(root, screen);

  const body = new FormData();
  body.append("user[email]", currentEmail(root));
  body.append("authenticity_token", csrfToken());

  try {
    const request = jsonRequest(button.dataset.path, { body }).then(
      (value) => ({ value }),
      (error) => ({ error }),
    );
    await wait(minimumBusyTime);
    const result = await request;
    if (result.error) throw result.error;

    if (result.value.response.ok) {
      retryAfter = result.value.data.retry_after;
      if (onSuccess) {
        onSuccess();
      } else {
        showFeedback(root, screen, successMessage);
      }
    } else {
      showFeedback(root, screen, root.dataset.membershipGenericError);
    }
  } catch (_error) {
    showFeedback(root, screen, root.dataset.membershipGenericError);
  } finally {
    setBusy(button, false);
    if (screen === "confirmation" && retryAfter !== undefined) {
      startConfirmationCooldown(button.closest("[data-resend-cooldown]"), retryAfter);
    }
  }
}

function openInitialDrawer(root) {
  if (root.dataset.initialScreenApplied === "true") return;
  const initialScreen = root.dataset.membershipInitialScreen;
  if (!initialScreen) return;

  if (initialScreen === "registration") showRegistration(root);
  if (initialScreen === "login") showLogin(root);

  if (root.dataset.membershipConfirmedNotice) {
    showFeedback(root, "login", root.dataset.membershipConfirmedNotice);
  }

  const drawer = root.closest("[data-drawer]");
  const trigger = document.querySelector('[data-drawer-open="membership-panel"]');
  openDrawer(drawer, trigger);
  root.dataset.initialScreenApplied = "true";
}

function initializeMembershipRoots() {
  document.querySelectorAll(rootSelector).forEach((root) => {
    updateIntroForm(root.querySelector(introFormSelector));
    updateRegistrationForm(root.querySelector(registrationFormSelector));
    updateLoginForm(root.querySelector(loginFormSelector));
    openInitialDrawer(root);
  });
}

document.addEventListener("click", function (event) {
  const register = event.target.closest?.('[data-membership-action="register"]');
  if (register) {
    showRegistration(register.closest(rootSelector));
    return;
  }

  const login = event.target.closest?.('[data-membership-action="login"]');
  if (login) {
    showLogin(login.closest(rootSelector));
    return;
  }

  const back = event.target.closest?.("[data-membership-back]");
  if (back) {
    const root = back.closest(rootSelector);
    if (back.closest('[data-membership-screen="login"]')) clearLoginDraft(root);
    showScreen(root, "intro", root.querySelector(introEmailSelector));
    return;
  }

  const forgot = event.target.closest?.("[data-membership-forgot-password]");
  if (forgot) {
    event.preventDefault();
    const root = forgot.closest(rootSelector);
    syncEditableCarriedEmail(root, "login");
    sendEmailInstructions(root, forgot, "login", null, {
      minimumBusyTime: resetInstructionMinimumBusyTime,
      onSuccess: () => showResetSent(root),
    });
    return;
  }

  const resetReturn = event.target.closest?.("[data-membership-reset-return]");
  if (resetReturn) {
    const root = resetReturn.closest(rootSelector);
    showScreen(root, "login", root.querySelector("[data-membership-login-heading]"));
    return;
  }

  const resend = event.target.closest?.("[data-membership-resend]");
  if (resend) {
    event.preventDefault();
    const root = resend.closest(rootSelector);
    sendEmailInstructions(root, resend, "confirmation", root.dataset.membershipConfirmationSent);
  }
});

document.addEventListener("input", function (event) {
  const root = event.target.closest?.(rootSelector);
  if (!root) return;

  if (event.target.matches(introEmailSelector)) updateIntroForm(root.querySelector(introFormSelector));

  if (event.target.matches(registrationEmailSelector)) syncEditableCarriedEmail(root, "registration");
  if (event.target.matches(loginEmailSelector)) syncEditableCarriedEmail(root, "login");

  if (event.target.closest(registrationFormSelector)) updateRegistrationForm(root.querySelector(registrationFormSelector));
  if (event.target.closest(loginFormSelector)) updateLoginForm(root.querySelector(loginFormSelector));
});

document.addEventListener("change", function (event) {
  const form = event.target.closest?.(registrationFormSelector);
  if (form) updateRegistrationForm(form);
});

document.addEventListener("submit", async function (event) {
  if (event.target.matches(introFormSelector)) {
    event.preventDefault();
    return;
  }

  if (event.target.matches(registrationFormSelector)) {
    event.preventDefault();
    const form = event.target;
    const root = form.closest(rootSelector);
    const submit = form.querySelector("[data-membership-submit]");
    updateRegistrationForm(form);
    if (submit.disabled) return;

    clearFeedback(root, "registration");
    setBusy(submit, true);
    try {
      const { response, data } = await jsonRequest(form.action, { body: new FormData(form) });
      if (response.ok && data.status === "confirmation_required") {
        form.querySelector("[data-membership-password]").value = "";
        startConfirmationCooldown(root.querySelector("[data-resend-cooldown]"), data.retry_after);
        showConfirmation(root);
      } else {
        showFeedback(root, "registration", errorMessages(data, root.dataset.membershipGenericError));
      }
    } catch (_error) {
      showFeedback(root, "registration", root.dataset.membershipGenericError);
    } finally {
      setBusy(submit, false);
      updateRegistrationForm(form);
    }
    return;
  }

  if (!event.target.matches(loginFormSelector)) return;

  event.preventDefault();
  const form = event.target;
  const root = form.closest(rootSelector);
  const submit = form.querySelector("[data-membership-login-submit]");
  const password = form.querySelector("[data-membership-login-password]");
  updateLoginForm(form);
  if (submit.disabled) return;

  clearFeedback(root, "login");
  setBusy(submit, true);
  try {
    const { response, data } = await jsonRequest(form.action, { body: new FormData(form) });
    if (response.ok && data.status === "signed_in") {
      if (data.redirect_url) {
        window.location.assign(data.redirect_url);
      } else {
        window.location.reload();
      }
      return;
    }

    password.value = "";
    if (data.status === "unconfirmed") {
      startConfirmationCooldown(root.querySelector("[data-resend-cooldown]"), data.retry_after);
      showConfirmation(root);
    } else {
      showFeedback(root, "login", errorMessages(data, root.dataset.membershipGenericError));
      focusAfterScreenChange(password);
    }
  } catch (_error) {
    showFeedback(root, "login", root.dataset.membershipGenericError);
  } finally {
    setBusy(submit, false);
    updateLoginForm(form);
  }
});

document.addEventListener("turbo:load", initializeMembershipRoots);
document.addEventListener("pageshow", initializeMembershipRoots);

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializeMembershipRoots);
} else {
  initializeMembershipRoots();
}

export { openMembershipDrawer };
